/**
 * Line Chart Widget (Story 5.5, Task 3 → updated Story 5.7/5.17)
 *
 * Renders time series data using Recharts LineChart.
 * Animations disabled for PDF compatibility (Story 5.12).
 * Supports annotation vertical markers (Story 5.17).
 *
 * 3-tier progressive degradation:
 * - colSpan ≤ 2: placeholder message (chart is illegible)
 * - colSpan 3-4: simplified (no legend, grid, dots; compact margins; annotations = dashed lines only)
 * - colSpan 5+: full rendering with all features + annotation labels + tooltip
 */

import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { ChartContainer } from "../../ui/chart";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { TimeSeriesData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isTimeSeriesData } from "../../../lib/widgetData";
import { metricKeysFromConfig } from "../../../lib/widgetCompatibility";
import { useChartWidgetShell, ChartMessage, NAVY_SERIES, seriesChannelSuffix } from "./chartWidgetShell";
import { ANNOTATION_COLORS } from "@provenleap/types/report-rendering";
import type { AnnotationConfig } from "@provenleap/types/report-rendering";
import { injectSyntheticAnnotationPoints, assignLabelOffsets } from "@provenleap/types/annotation-utils";

interface LineChartWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<TimeSeriesData>;
}

function formatDateTick(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Story 35.5 — humanize a Meta per-attribution-window metric key for the
 * Recharts legend (e.g. `action_purchase_7d_click` → "Purchases (7d click)").
 *
 * Returns the original `key` unchanged when no attribution-window suffix
 * is detected, preserving legend rendering for all non-Meta metrics. The
 * window-suffix matching is intentionally narrow so we don't accidentally
 * mangle metric keys that happen to contain "_click" or "_view"
 * substrings.
 */
export function humanizeAttributionWindowMetricKey(key: string): string {
  const match = key.match(/^(.*)_(1d_click|7d_click|28d_click|1d_view)$/);
  if (!match) return key;
  const [, basePrefix, window] = match;
  // Round 1 code review (M1): handle both `action_omni_` AND raw `omni_`
  // prefixes (the latter may appear in future metric expansions; we strip
  // the prefix and add "Omni " for legend clarity regardless of the
  // wrapping prefix).
  const baseLabel = (basePrefix ?? "")
    .replace(/^action_omni_/, "Omni ")
    .replace(/^omni_/, "Omni ")
    .replace(/^action_/, "")
    .replace(/_/g, " ");
  const titled = baseLabel
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  const windowLabel = window === "1d_click"
    ? "1d click"
    : window === "7d_click"
      ? "7d click"
      : window === "28d_click"
        ? "28d click"
        : "1d view";
  return `${titled} (${windowLabel})`;
}

/** Abbreviated tick for compact mode */
function formatDateTickShort(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", { month: "narrow", day: "numeric" });
}

/** Auto-calculate Y-axis width from longest formatted value in data */
function getYAxisWidth(points: Record<string, unknown>[], metrics: string[]): number {
  let maxLen = 0;
  for (const point of points) {
    for (const metric of metrics) {
      const val = point[metric];
      if (typeof val === "number") {
        const formatted = new Intl.NumberFormat("en-US", { notation: "compact" }).format(val);
        maxLen = Math.max(maxLen, formatted.length);
      }
    }
  }
  return Math.max(30, maxLen * 8 + 8);
}

const COMPACT_MARGINS = { top: 5, right: 5, bottom: 5, left: 5 };
const FULL_MARGINS = { top: 20, right: 20, bottom: 5, left: 0 };
const LABEL_STAGGER_PX = 14;

interface LineChartConfig {
  metrics?: string[];
  xAxis?: string;
  annotations?: AnnotationConfig[];
}

function AnnotationTooltip({
  active,
  payload,
  label,
  annotationMap,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string | number; name?: string; value?: number | string | null; color?: string }>;
  label?: string;
  annotationMap: Map<string, AnnotationConfig>;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const ann = label ? annotationMap.get(label) : undefined;

  return (
    <div className="bg-popover border border-border rounded-md shadow-md p-2 text-xs">
      <p className="font-medium mb-1">{formatDateTick(label ?? "")}</p>
      {payload.map((entry) =>
        entry.value != null ? (
          <p key={String(entry.dataKey)} style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === "number" ? entry.value.toLocaleString("en-US") : entry.value}
          </p>
        ) : null,
      )}
      {ann && (
        <div className="border-t border-border mt-1.5 pt-1.5 flex items-start gap-1">
          <span
            className="w-2 h-2 rounded-full mt-0.5 shrink-0"
            style={{ backgroundColor: ANNOTATION_COLORS[ann.color] }}
          />
          <div>
            <span className="font-semibold">{ann.label}</span>
            {ann.description && (
              <p className="text-muted-foreground mt-0.5">{ann.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function LineChartWidget({ widget, data }: LineChartWidgetProps) {
  const config = widget.config as LineChartConfig;
  // Normalize the metric binding (string[] / object[] / metricKey) to plain keys
  // so a preset/panel-stored OBJECT shape still maps to data-series dataKeys.
  const boundMetrics = metricKeysFromConfig(widget.config as Record<string, unknown>);
  const colSpan = widget.position.colSpan;
  const annotations = useMemo(() => config.annotations ?? [], [config.annotations]);
  const { compact, shell } = useChartWidgetShell(widget.config);

  if (data?.loading) {
    return shell("line-chart", widget.title, <div className="h-full min-h-[60px] w-full animate-pulse rounded bg-muted" />);
  }
  if (data?.error) {
    return shell("line-chart", widget.title, <ChartMessage role="alert" testId="line-chart-error">{data.error}</ChartMessage>, { centered: true });
  }

  const chartData = data?.data;
  if (!chartData || !isTimeSeriesData(chartData) || chartData.points.length === 0) {
    return shell("line-chart", widget.title, <ChartMessage testId="line-chart-no-data">No data available</ChartMessage>, { centered: true });
  }
  // An UNBOUND chart has no configured metrics — derive the series from the data
  // itself (every numeric key of the first point except `date`) so sample/preview
  // data still draws lines instead of an empty axis.
  const metrics =
    boundMetrics.length > 0
      ? boundMetrics
      : Object.keys(chartData.points[0] ?? {}).filter(
          (k) => k !== "date" && typeof (chartData.points[0] as Record<string, unknown>)[k] === "number",
        );
  if (colSpan <= 2 && !compact) {
    return shell("line-chart", widget.title, <ChartMessage testId="chart-placeholder">Widen widget to view chart</ChartMessage>, { centered: true });
  }

  // Appearance > "Axis labels" (the chart-renamed Text group): color / font /
  // size applied to the X & Y tick labels. Empty fields keep the defaults.
  const axisStyle = (widget.config?.style ?? {}) as { textColor?: string; fontFamily?: string; fontSize?: number; seriesColors?: Record<string, string>; seriesLabels?: Record<string, string>; legendFontSize?: number; legendMarker?: "line" | "dot" | "none"; legendMarkerSize?: number };
  const axisTick = (defaultSize: number) => ({
    fontSize: typeof axisStyle.fontSize === "number" ? axisStyle.fontSize : defaultSize,
    fill: axisStyle.textColor ?? "hsl(var(--muted-foreground))",
    ...(axisStyle.fontFamily ? { fontFamily: axisStyle.fontFamily } : {}),
  });
  // Per-series colour (Appearance > Line colors): user pick per metric, else the
  // default navy palette by index.
  const seriesColors = axisStyle.seriesColors ?? {};
  const seriesColor = (metric: string, i: number) => seriesColors[metric] ?? NAVY_SERIES[i % NAVY_SERIES.length];
  // Per-series label (Appearance > Line labels): user-friendly name in the legend;
  // falls back to the humanized metric key. When the chart mixes integrations, a
  // " · <Channel>" suffix is appended so each line's source is clear.
  const seriesLabels = axisStyle.seriesLabels ?? {};
  const channelSuffix = seriesChannelSuffix(widget.config as Record<string, unknown>, boundMetrics);
  const seriesLabel = (metric: string) =>
    (seriesLabels[metric]?.trim() || humanizeAttributionWindowMetricKey(metric)) + channelSuffix(metric);
  // Legend text size (Appearance > Lines > Legend size); default 11.
  const legendFontSize = typeof axisStyle.legendFontSize === "number" ? axisStyle.legendFontSize : 11;
  // Legend marker (Appearance > Lines > Legend marker): line (default) / dot / none.
  // The built-in Recharts icon doesn't vertically center a large circle against the
  // text, so for DOT and NONE we render our OWN inline-centered marker via a
  // formatter and hide the built-in icon (iconSize 0). LINE keeps the built-in
  // plainline (it centers fine).
  const legendMarker = axisStyle.legendMarker ?? "line";
  const markerSize = typeof axisStyle.legendMarkerSize === "number" ? axisStyle.legendMarkerSize : undefined;
  const legendIconType = "plainline" as const;
  const legendIconSize = legendMarker === "line" ? markerSize : 0;
  const dotPx = markerSize ?? 8;
  const legendFormatter =
    legendMarker === "dot"
      ? (value: string, entry: { color?: string }) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, verticalAlign: "middle" }}>
            <span style={{ width: dotPx, height: dotPx, borderRadius: "9999px", background: entry?.color ?? "currentColor", display: "inline-block" }} />
            <span>{value}</span>
          </span>
        )
      : legendMarker === "none"
        ? (value: string, entry: { color?: string }) => (
            <span style={{ color: entry?.color ?? "inherit" }}>{value}</span>
          )
        : undefined;

  // Minimal chrome when the widget is narrow (colSpan) OR short (height-compact,
  // e.g. the scaled preview frame): no grid/legend/axis clutter.
  const isCompact = colSpan <= 4 || compact;
  const showAnnotationLabels = colSpan >= 5 && !compact;
  const hasComparison = Boolean(chartData.comparisonPoints && chartData.comparisonPoints.length > 0);

  // Build comparison metric keys for synthetic point injection
  const comparisonKeys = hasComparison ? metrics.map((m) => `prev_${m}`) : [];

  // Merge primary and comparison data for Recharts (index-aligned)
  const baseData = hasComparison
    ? chartData.points.map((point, i) => {
        const compPoint = chartData.comparisonPoints![i];
        const merged: Record<string, number | string> = { ...point };
        if (compPoint) {
          for (const metric of metrics) {
            merged[`prev_${metric}`] = compPoint[metric] as number;
          }
        }
        return merged;
      })
    : chartData.points;

  // Inject synthetic data points for annotations on missing dates
  const mergedData = useMemo(
    () => injectSyntheticAnnotationPoints(baseData, annotations, metrics, comparisonKeys),
    [baseData, annotations, metrics, comparisonKeys],
  );

  const yAxisWidth = isCompact ? 30 : getYAxisWidth(mergedData, metrics);

  // Build date-to-index map and label offsets for annotations
  const dateToIndex = useMemo(() => {
    const map = new Map<string, number>();
    mergedData.forEach((p, i) => map.set(p.date as string, i));
    return map;
  }, [mergedData]);

  const labelOffsets = useMemo(
    () => assignLabelOffsets(annotations, dateToIndex),
    [annotations, dateToIndex],
  );

  // Annotation tooltip lookup map
  const annotationMap = useMemo(
    () => new Map(annotations.map((a) => [a.date, a])),
    [annotations],
  );

  return shell(
    "line-chart",
    widget.title,
    <div className="min-h-0 w-full flex-1" role="img" aria-label={`Line chart showing ${metrics.join(", ")} over time`}>
      <ChartContainer config={{}} className="h-full w-full">
          <LineChart data={mergedData} margin={isCompact ? COMPACT_MARGINS : FULL_MARGINS}>
            {!isCompact && <CartesianGrid stroke="hsl(214 25% 70% / 0.18)" vertical={false} />}
            <XAxis
              dataKey="date"
              tickFormatter={isCompact ? formatDateTickShort : formatDateTick}
              tick={axisTick(isCompact ? 9 : 11)}
              tickLine={false}
              axisLine={false}
              tickMargin={6}
            />
            <YAxis
              tick={axisTick(isCompact ? 9 : 11)}
              width={yAxisWidth}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
            />
            {!isCompact && (
              <Tooltip
                content={<AnnotationTooltip annotationMap={annotationMap} />}
              />
            )}
            {!isCompact && (hasComparison || metrics.length > 1) && <Legend wrapperStyle={{ fontSize: legendFontSize, paddingTop: 24 }} iconType={legendIconType} iconSize={legendIconSize} formatter={legendFormatter} verticalAlign="bottom" />}
            {metrics.map((metric, i) => (
              <Line
                key={metric}
                dataKey={metric}
                // Legend name: the user's custom label (Appearance > Line labels)
                // or the humanized metric key. Comparison mode keeps the two-series
                // "Current/Previous" naming.
                name={hasComparison ? "Current Period" : seriesLabel(metric)}
                stroke={seriesColor(metric, i)}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            {hasComparison && metrics.map((metric, i) => (
              <Line
                key={`prev_${metric}`}
                dataKey={`prev_${metric}`}
                name="Previous Period"
                // Same hue as its current-period line (user pick or default), dashed.
                stroke={seriesColor(metric, i)}
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            {/* Annotations — placed AFTER Line elements for z-index (v3) */}
            {annotations.map((ann) => (
              <ReferenceLine
                key={ann.id}
                x={ann.date}
                stroke={ANNOTATION_COLORS[ann.color]}
                strokeDasharray="5 5"
                strokeWidth={1}
                ifOverflow="visible"
                aria-label={ann.label}
                label={
                  showAnnotationLabels
                    ? {
                        value: ann.label,
                        position: "insideTopRight",
                        angle: -90,
                        fill: ANNOTATION_COLORS[ann.color],
                        fontSize: 11,
                        offset: 10 + (labelOffsets.get(ann.id) ?? 0) * LABEL_STAGGER_PX,
                      }
                    : undefined
                }
              />
            ))}
          </LineChart>
      </ChartContainer>
    </div>,
  );
}
